import { CheckIcon, Edit2Icon, EyeOffIcon } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip";
import { useProject } from "../../../data/project";
import { Button } from "../../../components/ui/button";
import Spinner from "../../../components/ui/spinner";
import Contenteditable from "../../../components/shared/contenteditable";

export function ProjectPageHeader(props: { preheader?: string; onEditName?: (updatedName: string) => Promise<void> }) {
    const { project } = useProject();
    const [editingName, setEditingName] = useState(false);
    const updateNameMutation = useMutation({
        mutationFn: async (name: string) => {
            if (!props.onEditName) {
                throw new Error("missing name updater prop");
            }
            await props.onEditName(name);
        },
    });
    function updateName(name: string) {
        updateNameMutation.mutate(name);
        setEditingName(false);
    }
    return (
        <div className={"flex flex-col"}>
            {props.preheader && <p className={"text-sm text-muted-foreground"}>{props.preheader}</p>}
            {!editingName && (
                <h2 className={"flex flex-row flex-wrap items-center gap-4"}>
                    {project.name}
                    {props.onEditName && !updateNameMutation.isPending && (
                        <Tooltip>
                            <TooltipTrigger>
                                <Button
                                    onClick={() => setEditingName(true)}
                                    variant={"ghost"}
                                    size={"sm2"}
                                    className={"-ms-2 px-2 font-medium text-muted-foreground"}
                                >
                                    <Edit2Icon className={"h-4 w-4"} />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit project name</TooltipContent>
                        </Tooltip>
                    )}
                    {updateNameMutation.isPending && <Spinner className={"h-5 w-5 text-muted-foreground"} />}
                    {project.confidential && (
                        <Tooltip delayDuration={100}>
                            <TooltipTrigger>
                                <div
                                    className={
                                        "flex origin-left cursor-help flex-row items-center gap-1 rounded-md border border-primary px-2 py-0.5 text-xs font-semibold text-primary opacity-65"
                                    }
                                >
                                    <EyeOffIcon className={"h-3 w-3"} strokeWidth={2.5} />
                                    Confidential
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                This project is visible only to select users in the organisation.
                                <br />
                                <span className={"text-xs text-muted-foreground"}>
                                    More access control is coming soon.
                                </span>
                            </TooltipContent>
                        </Tooltip>
                    )}
                </h2>
            )}

            {editingName && (
                <TitleInput name={project.name} updateName={updateName} stopEditing={() => setEditingName(false)} />
            )}
        </div>
    );
}

function TitleInput(props: { name: string; updateName: (name: string) => void; stopEditing: () => void }) {
    const valueHolder = useMemo(() => ({ name: props.name }), [props.name]);
    return (
        <div className={"flex flex-row items-center gap-2"}>
            <h2 className={"-my-0.5 -ms-2 self-start rounded-md bg-input px-2 py-0.5"}>
                <Contenteditable
                    className={"outline-none"}
                    value={props.name}
                    onChange={(v) => (valueHolder.name = v)}
                />
            </h2>
            <Button
                onClick={() => props.updateName(valueHolder.name)}
                size={"sm2"}
                className={"animate-in slide-in-from-left-1"}
            >
                <CheckIcon className={"me-1 h-5 w-5"} /> Done
            </Button>
            <Button
                onClick={props.stopEditing}
                size={"sm2"}
                variant={"ghost"}
                className={"animate-in slide-in-from-left-2"}
            >
                Cancel
            </Button>
        </div>
    );
}
